"use client";

import { useTranslations } from "next-intl";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui";
import { ProductReviews } from "../ProductReviews";

interface ProductSpecificationTabsProps {
  isService?: boolean;
  description?: string;
  fullDescription?: string;
  specifications?: Array<{ name: string; value: string }>;
  shippingCostType?: "free" | "negotiable";
  serviceTime?: string;
  serviceLocation?: Array<{
    name: string;
    latitude?: string;
    longitude?: string;
  }>;
  isOffline?: boolean;
  isOnline?: boolean;
  showShippingCostType?: boolean;
  productId?: string;
}

export const ProductSpecificationTabs = ({
  isService,
  description,
  fullDescription,
  specifications,
  shippingCostType,
  serviceTime,
  serviceLocation,
  isOffline,
  isOnline,
  showShippingCostType = true,
  productId,
}: ProductSpecificationTabsProps) => {
  const t = useTranslations("shop");
  return (
    <Tabs syncWithHash={false} defaultValue="specification">
      {/* Desktop anchors the tabs on a full-width hairline; mobile unchanged. */}
      <div className="md:border-b md:border-stroke-subtle">
        <TabsList variant={"bordered"} className="w-fit">
          <TabsTrigger variant={"bordered"} value="specification">
            {t("specs.tabSpecification")}
          </TabsTrigger>
          <TabsTrigger variant={"bordered"} value="description">
            {t("specs.tabDescription")}
          </TabsTrigger>
          {productId && (
            <TabsTrigger variant={"bordered"} value="reviews">
              {t("specs.tabReviews")}
            </TabsTrigger>
          )}
        </TabsList>
      </div>
      <TabsContent value="specification">
        {isService ? (
          <div className="py-4 md:py-6">
            <ul className="space-y-2 md:space-y-3 md:text-base">
              <li>
                <p>
                  <span className="font-semibold text-base">
                    {t("specs.serviceStatus")}
                  </span>{" "}
                  <span className="font-normal">
                    {[
                      isOffline && t("specs.serviceStatusOffline"),
                      isOnline && t("specs.serviceStatusOnline"),
                    ]
                      .filter(Boolean)
                      .join(" | ")}
                  </span>
                </p>
              </li>
              <li>
                <p>
                  <span className="font-semibold text-base">
                    {t("specs.serviceTime")}
                  </span>{" "}
                  <span className="font-normal">
                    {serviceTime || t("specs.notProvided")}
                  </span>
                </p>
              </li>
              <li>
                <p>
                  <span className="font-semibold text-base">
                    {t("specs.serviceLocation")}
                  </span>{" "}
                  <span className="font-normal">
                    {serviceLocation && serviceLocation.length > 0
                      ? serviceLocation
                          .map((location) => location.name)
                          .join(" - ")
                      : t("specs.notProvided")}
                  </span>
                </p>
              </li>
            </ul>
          </div>
        ) : (
          <div className="py-4 space-y-3 md:py-6 md:space-y-4">
            {description && (
              <p className="text-text-secondary text-sm md:text-base md:leading-relaxed">
                {description}
              </p>
            )}
            {specifications && specifications.length > 0 && (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                {specifications
                  .filter((spec) => spec?.name?.trim() && spec?.value?.trim())
                  .map((spec) => (
                    <div
                      key={`${spec.name}-${spec.value}`}
                      className="bg-surface space-y-2 rounded-md w-full text-center py-4"
                    >
                      <p className="text-text-label text-sm font-semibold truncate px-2">
                        {spec.name}
                      </p>
                      <p className="text-text-label text-sm truncate px-2">
                        {spec.value}
                      </p>
                    </div>
                  ))}
              </div>
            )}
            {showShippingCostType && shippingCostType && (
              <p className="text-text-secondary text-xs">
                {shippingCostType === "free"
                  ? t("specs.shippingFree")
                  : t("specs.shippingNegotiable")}
              </p>
            )}
          </div>
        )}
      </TabsContent>

      <TabsContent value="description">
        <p className="py-4 text-sm text-text-secondary md:max-w-3xl md:py-6 md:text-base md:leading-relaxed">
          {fullDescription || description || t("specs.noDescription")}
        </p>
      </TabsContent>
      {productId && (
        <TabsContent value="reviews">
          <ProductReviews productId={productId} />
        </TabsContent>
      )}
    </Tabs>
  );
};
